import { useCallback, useState, useEffect, useRef } from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { Box, Paper } from '@mui/material';
import {
  GridCallbackDetails,
  GridSortModel,
  GridPaginationModel,
} from '@mui/x-data-grid-pro';
import {
  DataGridHeader,
  DataGridTitle,
  DataGridFilters,
  DataGridSearch,
} from '../../tables/DataGridPro';

import { SortModel } from '../../../data/models/sort-model.model';

import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useDebouncedRefetch } from '@/hooks/useDebouncedRefetch';

import { TripLegEventType } from '@/data/events/trip-leg';

import realtimeUtils from '../../../services/webSocketService/realtimeUtils';

import {
  fetchTripManagementTodaysTripsAsync,
  selectAllTripLegs,
  selectTripManagementTodaysTrips,
  selectLoadingTripManagementTodaysTrips,
  selectLoadedTripManagementTodaysTrips,
  selectTripLegEventQueue,
  removeTripLegEventFromQueue,
} from '../../../data/slices/trip-leg.slice';
import * as lookupSlice from '../../../data/lookup.slice';

import { TodaysTripsTable } from './TodaysTripsTable';
import SelectMenu from '../../forms/SelectMenu';
import {
  TripLegExceptionSelection,
  TripLegExceptionBC,
} from '../../../data/models/trip-leg-exception.model';
import { TripLegSearchMenu } from '@/data/requests/trip-legs';
import type { TripLegSearchResponseItem } from '@/data/models/trip-leg-search.model';
import type { PagedList } from '../../../data/models/paged-list.model';
import type { FilterModel } from '@/data/models/filters.model';

const PREFIX = 'TodaysTrips';

const classes = {
  container: `${PREFIX}-container`,
  tableContainer: `${PREFIX}-tableContainer`,
};

const StyledBox = styled(Paper)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: '24px',
    paddingRight: '32px',
    paddingLeft: '32px',
  },
  [`& .${classes.tableContainer}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
}));

const DEFAULT_SORT_MODEL: SortModel = {
  column: 'isEscalated,pickUpDueTimeUTC',
  direction: 'desc,asc',
};

const TodaysTrips = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const activeSearchRequestRef = useRef<AbortController | null>(null);
  const hasFetchedInitialDataRef = useRef(false);
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const { control, watch } = useForm({
    mode: 'onTouched',
  });
  const tripLegExceptionFilter = watch('tripLegExceptionFilter');

  const tripLegExceptions: Partial<TripLegExceptionSelection>[] = [
    {
      tripLegExceptionBC: 'all',
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('todaysTrips.allExceptions'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.OpenSameDay,
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('tripLegException.open'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.DriverLate,
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('tripLegException.driverLate'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.NoDriver,
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('tripLegException.noDriver'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.DriverNotOnline,
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('tripLegException.driverNotOnline'),
    },
    {
      tripLegExceptionBC: TripLegExceptionBC.EtaNotUpdating,
      menu: TripLegSearchMenu.TODAYS_TRIPS,
      tripLegExceptionName: t('tripLegException.etaNotUpdating'),
    },
  ];

  const allTripLegs = useAppSelector(selectAllTripLegs);
  const tripManagementTodaysTripLegs = useAppSelector(
    selectTripManagementTodaysTrips,
  );
  const loadingTripManagementTodaysTrips = useAppSelector(
    selectLoadingTripManagementTodaysTrips,
  );

  const tripLegEventQueue = useAppSelector(selectTripLegEventQueue);

  const [page, setPage] = useState(1);
  // Deviate from standard page size for this page specifically F1V2-3749
  const [pageSize, setPageSize] = useState<number>(100);
  const [sortModel, setSortModel] = useState<SortModel>(DEFAULT_SORT_MODEL);

  const fetchTripLegs = useCallback(() => {
    activeSearchRequestRef.current?.abort();
    const abortController = new AbortController();
    activeSearchRequestRef.current = abortController;

    const syncIdsOnly = hasFetchedInitialDataRef.current;
    hasFetchedInitialDataRef.current = true;

    dispatch(
      fetchTripManagementTodaysTripsAsync({
        menu: TripLegSearchMenu.TODAYS_TRIPS,
        search: searchTerm || undefined,
        todayDate: DateTime.local().toFormat('yyyy-MM-dd'),
        page,
        pageSize,
        sort:
          sortModel?.column === DEFAULT_SORT_MODEL.column
            ? undefined
            : sortModel.column,
        sortOrder:
          sortModel?.column === DEFAULT_SORT_MODEL.column
            ? undefined
            : sortModel.direction,
        tripLegExceptionBCs:
          tripLegExceptionFilter !== 'all'
            ? [tripLegExceptionFilter]
            : undefined,
        syncIdsOnly,
        signal: abortController.signal,
      }),
    );
  }, [dispatch, page, pageSize, sortModel, searchTerm, tripLegExceptionFilter]);

  // Counts requests rather than flagging them, so a realtime re-sync
  // requested right after a fetch isn't dropped (see useDebouncedRefetch)
  const requestRefetch = useDebouncedRefetch(fetchTripLegs, 200);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handlePaginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== tripManagementTodaysTripLegs.currentPage - 1) {
      setPage(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
    }
  };

  const handleSortModelChange = (
    model: GridSortModel,
    _details: GridCallbackDetails,
  ) => {
    let newModel: SortModel | null = null;
    if (model?.length) {
      if (model[0].field === 'pickUpDueTimeUTC') {
        newModel = {
          column: 'appointmentDate,pickUpDueTimeUTC',
          direction: model[0].sort === 'desc' ? 'desc,desc' : 'asc,asc',
        };
      } else {
        newModel = {
          column: model[0].field,
          direction: model[0].sort,
        };
      }
    } else {
      newModel = DEFAULT_SORT_MODEL;
    }
    setPage(1);
    if (newModel) {
      setSortModel(newModel);
    }
  };

  // General refetch of page for pagination, sort, search, and filter changes.
  useEffect(() => {
    requestRefetch();
  }, [
    fetchTripLegs,
    page,
    pageSize,
    sortModel,
    searchTerm,
    tripLegExceptionFilter,
  ]);

  // Keyed on both the adapter and the queue. The websocket handler enqueues
  // an event and writes its entity in the same batched render, so this
  // always sees the updated entity. Keying on the queue too matters for
  // events whose entity write is a no-op, most importantly a new trip leg's
  // opened event: new trip legs stay out of the store until a fetch loads
  // them (see tripLegOpened in trip-leg.slice.ts), so the adapter alone
  // wouldn't change and the page would never refetch to show them.
  useEffect(() => {
    if (!tripLegEventQueue?.length) return;

    const eventTypesToRefetchOn = [
      TripLegEventType.TripLegDrafted,
      TripLegEventType.TripLegOpened,
      TripLegEventType.TripLegUnderDiscussion,
      TripLegEventType.TripLegException,
      TripLegEventType.TripLegEscalated,
    ];
    const cancellationEventTypes = [
      TripLegEventType.TripLegCanceled,
      TripLegEventType.TripLegCanceledLate,
    ];
    const shouldRefresh = tripLegEventQueue.some((event) => {
      const detailType = event.detailType as TripLegEventType;
      if (eventTypesToRefetchOn.includes(detailType)) {
        return true;
      }

      // The server pushes its trip leg counts (the nav badge) after they're
      // recalculated, which can be after the refetch for a new trip leg's
      // event already ran and missed it. Re-sync whenever today's count no
      // longer matches this page's, so the two can't stay out of step. A
      // search or exception filter narrows the page, so it can't be compared.
      if (detailType === TripLegEventType.TripLegCountsUpdated) {
        const todaysCount = event.detail?.today;
        return (
          !searchTerm?.trim() &&
          (!tripLegExceptionFilter || tripLegExceptionFilter === 'all') &&
          typeof todaysCount === 'number' &&
          todaysCount !== tripManagementTodaysTripLegs.itemCount
        );
      }

      // Only refresh on cancellation if the trip leg is currently on the page.
      if (cancellationEventTypes.includes(detailType)) {
        return tripManagementTodaysTripLegs.items.some(
          (tripLeg) =>
            tripLeg.tripLegReferenceID === event.detail?.tripLegReferenceID,
        );
      }
      return false;
    });

    if (shouldRefresh) {
      requestRefetch();
    }
    dispatch(removeTripLegEventFromQueue(tripLegEventQueue.length));
  }, [
    allTripLegs,
    tripLegEventQueue,
    fetchTripLegs,
    dispatch,
    tripManagementTodaysTripLegs,
  ]);

  useEffect(() => {
    dispatch(
      lookupSlice.fetchLookupItemsAsync('transportationProviderPhoneUse'),
    );
  }, []);

  useEffect(() => {
    return () => {
      activeSearchRequestRef.current?.abort();
    };
  }, []);

  return (
    <>
      <StyledBox elevation={2}>
        <Box className={classes.container}>
          <DataGridHeader>
            <DataGridTitle>{t('todaysTrips.todaysTrips')}</DataGridTitle>

            <DataGridFilters>
              <DataGridSearch
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                placeholder={t('todaysTrips.searchPlaceholder')}
              />
              <SelectMenu
                label={t('todaysTrips.exception')}
                name="tripLegExceptionFilter"
                values={tripLegExceptions}
                valueAttr="tripLegExceptionBC"
                labelAttr="tripLegExceptionName"
                control={control}
                defaultValue={tripLegExceptions[0]?.tripLegExceptionBC}
              />
            </DataGridFilters>
          </DataGridHeader>
          <Box className={classes.tableContainer}>
            <TodaysTripsTable
              todaysTripLegs={
                tripManagementTodaysTripLegs as unknown as PagedList<TripLegSearchResponseItem>
              }
              todaysTripLegsLoading={loadingTripManagementTodaysTrips}
              page={page}
              pageSize={pageSize}
              sortModel={sortModel}
              handlePaginationChange={handlePaginationChange}
              handleSortModelChange={handleSortModelChange}
            />
          </Box>
        </Box>
      </StyledBox>
      <Box sx={{ height: 24 }} />
    </>
  );
};

export { TodaysTrips };
